import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { SupabaseProvider } from './components/admin/SupabaseContext'
import ReactQueryProvider from './components/providers/ReactQueryProvider'
import CookiesProviderWrapper from './components/CookiesProviderWrapper'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { WebVitals } from './components/WebVitals'
import Header from './components/ui/Header'
import Footer from './components/ui/Footer'
import CookieConsent from './components/ui/CookieConsent'
import GoogleAnalytics from './components/ui/GoogleAnalytics'
import { preloadServiceImages } from './utils/imagePreloader'

// Only import Home page statically (for initial load)
import Home from './pages/Home'

// Lazy load all other pages
const About = lazy(() => import('./pages/About'))
const Services = lazy(() => import('./pages/Services'))
const Insights = lazy(() => import('./pages/Insights'))
const Contact = lazy(() => import('./pages/Contact'))
const AdminIndex = lazy(() => import('./pages/admin/AdminIndex'))
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminContent = lazy(() => import('./pages/admin/AdminContent'))
const AdminManager = lazy(() => import('./pages/admin/AdminManager'))
const NotFound = lazy(() => import('./pages/NotFound'))

// Lazy load Individual Service Pages
const EmployeeLedStrategicImprovement = lazy(() => import('./pages/services/EmployeeLedStrategicImprovement'))
const OrganizationalChangeAlignment = lazy(() => import('./pages/services/OrganizationalChangeAlignment'))
const ExecutiveCoachingLeadershipDevelopment = lazy(() => import('./pages/services/ExecutiveCoachingLeadershipDevelopment'))
const ExperientialKnowledgeCapture = lazy(() => import('./pages/services/ExperientialKnowledgeCapture'))
const RapidCapabilityDevelopment = lazy(() => import('./pages/services/RapidCapabilityDevelopment'))
const DigitalOperationalExcellence = lazy(() => import('./pages/services/DigitalOperationalExcellence'))

// Layout wrapper that conditionally shows Header/Footer based on route
function AppLayout() {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')

  // Preload service images to prevent re-fetching on navigation
  useEffect(() => {
    preloadServiceImages()
  }, [])

  return (
    <>
      <CookieConsent />
      <GoogleAnalytics />
      <WebVitals />

      {!isAdminRoute && <Header />}
      <main>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-gray-600">Loading...</div></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/contact" element={<Contact />} />

            {/* Individual Service Routes */}
            <Route path="/services/employee-led-strategic-improvement" element={<EmployeeLedStrategicImprovement />} />
            <Route path="/services/organizational-change-alignment" element={<OrganizationalChangeAlignment />} />
            <Route path="/services/executive-coaching-leadership-development" element={<ExecutiveCoachingLeadershipDevelopment />} />
            <Route path="/services/experiential-knowledge-capture" element={<ExperientialKnowledgeCapture />} />
            <Route path="/services/rapid-capability-development" element={<RapidCapabilityDevelopment />} />
            <Route path="/services/digital-operational-excellence" element={<DigitalOperationalExcellence />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminIndex />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/content" element={<AdminContent />} />
            <Route path="/admin/manager" element={<AdminManager />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminRoute && <Footer />}

      <Toaster position="bottom-right" />
    </>
  )
}

function App() {
  return (
    <BrowserRouter>
      <CookiesProviderWrapper>
        <ReactQueryProvider>
          <SupabaseProvider>
            <ErrorBoundary>
              <AppLayout />
            </ErrorBoundary>
          </SupabaseProvider>
        </ReactQueryProvider>
      </CookiesProviderWrapper>
    </BrowserRouter>
  )
}

export default App
